import { Suspense, useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { useJoinFunnelAnalytics } from '@components/Analytics/useJoinFunnelAnalytics'
import { usePublicTownsPageAnalyticsEvent } from '@components/Analytics/usePublicTownPageAnalyticsEvent'
import { ErrorBoundary } from '@components/ErrorBoundary/ErrorBoundary'
import { RequirementsModal } from '@components/Modals/RequirementsModal'
import { minterRoleId } from '@components/SpaceSettingsPanel/rolePermissions.const'
import { TownReviewsSection } from '@components/TownPageLayout/TownReviewsSection'
import { FadeInBox } from '@components/Transitions'
import { ImageVariants, useImageSource } from '@components/UploadImage/useImageSource'
import { contractAddressFromSpaceId, isSpaceStreamId } from '@towns-protocol/sdk'
import { dlog } from '@towns-protocol/utils'
import { Box, Button, Heading, Icon, IconButton, Paragraph, Stack, Text } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { useEntitlements } from 'hooks/useEntitlements'
import { useMobile } from 'hooks/useMobile'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { PATHS } from 'routes'
import { MembershipPriceDisplay } from 'routes/EditSpaceMetadata/components/MembershipPriceDisplay'
import { useFetchSpaceMetadata } from 'routes/EditSpaceMetadata/hooks/useFetchSpaceMetadata'
import { buildSpaceMetadata } from 'routes/EditSpaceMetadata/hooks/useSpaceMetadataProgress'
import type { EditTownMetadataFormValues } from 'routes/EditSpaceMetadata/schema/spaceMetadata.schema'
import { buildSpaceMetadataPayload } from 'routes/EditSpaceMetadata/utils/editSpaceMetadataUtils'
import { JoinLoginButton } from 'routes/PublicTownPage/JoinLoginButton'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    SpaceIdFromSpaceAddress,
    useConnectivity,
    useContractSpaceInfoWithoutClient,
} from 'use-towns-client'
import { isAddress, zeroAddress } from 'viem'

import { EntitlementsDisplayV2 } from './components/EntitlementsDisplay'
import { ShareLinkButton } from './components/ShareTownButton'
import { TownAbout } from './components/TownAbout'
import { TownChannelList } from './components/TownChannelList'
import { TownCreator } from './components/TownCreator'
import { TownFAQ } from './components/TownFAQ'
import { TownFeatures, TownFeaturesWithPrice } from './components/TownFeatures'
import { TownHeader } from './components/TownHeader'
import { TownInfo } from './components/TownInfo'
import { TownMedia } from './components/TownMedia'

const log = dlog('app:public-town')
log.enabled = true

type Props = {
    isPreview?: boolean
    onClosePreview?: () => void
    previewData?: EditTownMetadataFormValues
}

export const TownPageV2 = (props: Props) => {
    return (
        <ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
            <Suspense
                fallback={
                    <Box absoluteFill centerContent>
                        <ButtonSpinner />
                    </Box>
                }
            >
                <_TownPageV2 {...props} />
            </Suspense>
        </ErrorBoundary>
    )
}

export const _TownPageV2 = (props: Props) => {
    const { isPreview, previewData } = props
    const { exploreId } = useParams<{ exploreId: string }>()
    const spaceIdFromPathname = useSpaceIdFromPathname()
    const _spaceId = exploreId || spaceIdFromPathname

    const navigate = useNavigate()

    const { isAuthenticated } = useConnectivity()

    // todo: refactor this
    const spaceId = useMemo(() => {
        if (_spaceId && isSpaceStreamId(_spaceId)) {
            return _spaceId
        }
        if (_spaceId && isAddress(_spaceId)) {
            return SpaceIdFromSpaceAddress(_spaceId)
        }
        return spaceIdFromPathname
    }, [_spaceId, spaceIdFromPathname])

    // todo: refactor this
    const spaceAddress = useMemo(() => {
        if (!spaceId) {
            return undefined
        }
        return isAddress(spaceId ?? '')
            ? spaceId
            : isSpaceStreamId(spaceId)
              ? contractAddressFromSpaceId(spaceId ?? '')
              : undefined
    }, [spaceId])

    usePublicTownsPageAnalyticsEvent({
        authenticated: isAuthenticated,
        overrideSpaceId: spaceId,
    })

    const { data: spaceData, isLoading: isLoadingSpaceInfo } =
        useContractSpaceInfoWithoutClient(spaceId)
    const { data: rawSpaceMetadata, isLoading: isLoadingSpaceMetadata } = useFetchSpaceMetadata(
        spaceAddress,
        { enabled: !isPreview },
    )

    // Use preview data if provided, otherwise use fetched data
    const metadata = useMemo(() => {
        if (previewData) {
            const serverMetadata = buildSpaceMetadataPayload(previewData)
            return buildSpaceMetadata(serverMetadata)
        }
        return buildSpaceMetadata(rawSpaceMetadata)
    }, [previewData, rawSpaceMetadata])

    // In preview mode, use media directly from form to preserve variants
    const media = previewData?.media ?? metadata.media

    const { category, features, faq, aboutCreator, creatorDisplayName, socialLinks } = metadata

    let { aboutTown, motto } = metadata
    // In preview mode, use form data directly; otherwise fall back to contract data
    if (previewData) {
        aboutTown = previewData.aboutTown || ''
        motto = previewData.motto || ''
    } else {
        aboutTown = aboutTown || spaceData?.longDescription || ''
        motto = motto || spaceData?.shortDescription || ''
    }

    const { imageSrc: townTokenImageSrc } = useImageSource(
        spaceId ?? '',
        ImageVariants.thumbnail1200,
    )

    const [params] = useSearchParams()

    const onBack = useCallback(() => {
        if (params.get('rel')) {
            navigate(-1)
        } else {
            navigate(`/${PATHS.EXPLORE}`)
        }
    }, [navigate, params])

    const { isTouch } = useDevice()
    const shouldShowBackButton = !isTouch || params.get('rel') === 'panel'
    // ------------------------------------------------------------
    // Requirements Modal
    // ------------------------------------------------------------

    const [showRequirementsModal, setShowRequirementsModal] = useState(false)
    const { data: entitlements } = useEntitlements(spaceId, minterRoleId)
    const { viewedGatedTownRequirementsModal } = useJoinFunnelAnalytics()

    const onShowRequirementsModal = useCallback(() => {
        if (spaceId) {
            viewedGatedTownRequirementsModal({
                spaceId: spaceId,
                meetsMembershipRequirements: false,
            })
            setShowRequirementsModal(true)
        }
    }, [spaceId, viewedGatedTownRequirementsModal])

    // ------------------------------------------------------------

    // Skip loading state only in preview mode; block on live
    if (!isPreview && (isLoadingSpaceInfo || isLoadingSpaceMetadata)) {
        return <></>
    }

    if (!spaceData && !isPreview) {
        return (
            <Box absoluteFill centerContent>
                <TownNotFoundBox />
            </Box>
        )
    }

    return (
        <>
            <TownPageSkeleton onBack={shouldShowBackButton ? onBack : undefined}>
                <TwoColumnsLayout
                    leftContent={
                        <Box
                            gap="x3"
                            position="relative"
                            paddingY={{ touch: 'x2', default: 'none' }}
                        >
                            <TownHeader
                                name={previewData?.name || spaceData?.name}
                                motto={motto}
                                category={category}
                                address={spaceAddress}
                            />
                            <TownMedia
                                key={isPreview ? 'preview' : 'live'}
                                media={media}
                                fallbackImageSrc={townTokenImageSrc}
                            />
                            {spaceId && spaceData?.networkId && (
                                <TownInfo
                                    spaceId={spaceId}
                                    networkId={spaceData.networkId}
                                    onShowRequirementsModal={onShowRequirementsModal}
                                />
                            )}
                            {isTouch && <TownFeatures features={features} />}
                            <TownAbout aboutTown={aboutTown} withDivider={features?.length > 0} />
                            <TownChannelList spaceId={spaceId} />
                            <TownCreator
                                aboutCreator={aboutCreator}
                                owner={spaceData?.owner ?? zeroAddress}
                                creatorDisplayName={creatorDisplayName}
                                socialLinks={socialLinks}
                            />
                            <TownFAQ faq={faq} />

                            <TownReviewsSection showSummary spaceId={spaceId} />
                        </Box>
                    }
                    rightContent={
                        <Stack gap="lg" width="350">
                            <TownFeaturesWithPrice features={features} spaceId={spaceId}>
                                {isPreview ? null : (
                                    <JoinTownButton
                                        entitlements={entitlements}
                                        spaceId={spaceId}
                                        leftContent={<MembershipPriceDisplay spaceId={spaceId} />}
                                    />
                                )}
                            </TownFeaturesWithPrice>
                            {spaceData && spaceData?.name && (
                                <ShareLinkButton
                                    spaceId={spaceData.networkId}
                                    name={spaceData.name}
                                />
                            )}
                        </Stack>
                    }
                />

                {isTouch && (
                    <FloatingTouchFeatures
                        entitlements={entitlements}
                        spaceId={spaceId}
                        isPreview={isPreview}
                    />
                )}

                {showRequirementsModal && spaceId && (
                    <RequirementsModal
                        title="Requirements to Join"
                        spaceId={spaceId}
                        entitlements={entitlements}
                        onClose={() => setShowRequirementsModal(false)}
                    />
                )}
            </TownPageSkeleton>
        </>
    )
}

const FloatingTouchFeatures = (props: {
    entitlements?: ReturnType<typeof useEntitlements>['data']
    spaceId: string | undefined
    isPreview?: boolean
}) => {
    const { entitlements, spaceId, isPreview } = props

    return (
        <Box position="sticky" bottom="none" insetX="sm">
            <TownFeaturesWithPrice
                features={null}
                spaceId={spaceId}
                roundedBottom="none"
                background="level2"
                paddingBottom="x3"
                paddingTop="md"
            >
                {isPreview ? null : (
                    <JoinTownButton
                        entitlements={entitlements}
                        spaceId={spaceId}
                        leftContent={<MembershipPriceDisplay spaceId={spaceId} />}
                    />
                )}
            </TownFeaturesWithPrice>
        </Box>
    )
}

const JoinTownButton = (props: {
    entitlements?: ReturnType<typeof useEntitlements>['data']
    spaceId: string | undefined
    leftContent?: React.ReactNode
}) => {
    const { entitlements, spaceId, leftContent } = props

    return (
        <Stack>
            {entitlements?.hasEntitlements && (
                <Box
                    background="lightHover"
                    borderRadius="xs"
                    paddingY="xs"
                    height="x4"
                    width="100%"
                    position="relative"
                >
                    <Box
                        horizontal
                        paddingX="sm"
                        gap="sm"
                        position="absolute"
                        width="100%"
                        alignItems="center"
                        overflow="hidden"
                    >
                        <Box height="x3" minWidth="none">
                            <EntitlementsDisplayV2 entitlements={entitlements} />
                        </Box>
                        <Box grow position="relative" justifyContent="center">
                            <Text grow whiteSpace="nowrap" color="gray2" size="sm">
                                Required digital assets
                            </Text>
                        </Box>
                    </Box>
                </Box>
            )}
            <JoinLoginButton
                leftContent={leftContent}
                spaceId={spaceId}
                horizontal={!entitlements?.hasEntitlements}
            />
        </Stack>
    )
}

const TwoColumnsLayout = (props: {
    leftContent: React.ReactNode
    rightContent: React.ReactNode
}) => {
    const isMobile = useMobile()
    if (isMobile) {
        return (
            <Stack grow gap="x4">
                {props.leftContent}
            </Stack>
        )
    }
    return (
        <Stack horizontal paddingY gap="x6" justifyContent="center">
            <Stack grow gap="x2" style={{ maxWidth: 650 }} paddingY="x1">
                {props.leftContent}
            </Stack>
            <Stack gap="lg" position="sticky" top="x2" alignSelf="start" style={{ minWidth: 300 }}>
                {props.rightContent}
            </Stack>
        </Stack>
    )
}

const TownPageSkeleton = (props: { onBack?: () => void; children: React.ReactNode }) => {
    const PAGE_MAX_WIDTH = 1050
    const isMobile = useMobile()

    if (isMobile) {
        return (
            <Stack grow gap="x4" position="relative">
                {props.onBack && (
                    <Box paddingTop="sm">
                        <IconButton
                            size="square_md"
                            background="level2"
                            icon="arrowLeft"
                            onClick={props.onBack}
                        />
                    </Box>
                )}
                <Box scroll absoluteFill>
                    <Box gap paddingX="md" position="relative">
                        {props.children}
                    </Box>
                </Box>
            </Stack>
        )
    }

    return (
        <Box grow horizontal paddingX>
            <Box
                grow
                height="height_md"
                alignItems="start"
                position="sticky"
                top="none"
                paddingTop="x2"
            >
                <Box minWidth="x4" position="sticky" top="none">
                    {props.onBack && (
                        <IconButton
                            size="square_md"
                            background="level2"
                            icon="arrowLeft"
                            onClick={props.onBack}
                        />
                    )}
                </Box>
            </Box>
            <Box grow flexBasis="100%" style={{ maxWidth: PAGE_MAX_WIDTH }}>
                {props.children}
            </Box>
            <Box grow>
                <Box minWidth="x4" />
            </Box>
        </Box>
    )
}

const TownNotFoundBox = () => (
    <FadeInBox centerContent gap="lg" maxWidth="400" data-testid="town-not-found-box">
        <Box padding background="error" borderRadius="sm">
            <Icon color="default" type="alert" size="square_md" />
        </Box>
        <Heading level={3}>Town not found</Heading>
        <Paragraph color="gray2" textAlign="center">
            The town is currently unreachable, please try again later.
        </Paragraph>
        <Box>
            <Link to="/">
                <Button size="button_md">Back to login</Button>
            </Link>
        </Box>
    </FadeInBox>
)

const ErrorFallbackComponent = (props: { error: Error }) => {
    return (
        <Box
            grow
            centerContent
            elevateReadability
            border="faint"
            padding="lg"
            gap="x4"
            rounded="sm"
        >
            <Icon type="alert" color="negative" size="square_lg" insetBottom="xs" />
            <Stack color="gray1" maxWidth="400">
                <Paragraph textAlign="center">The townpage couldn&apos;t be loaded</Paragraph>
            </Stack>
        </Box>
    )
}
